<template>
  <div class="dashboard-page">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2 class="dashboard-title">综合数据看板</h2>
      <div class="header-right">
        <el-button size="small" :loading="loadingAll" @click="refreshAll()">刷新数据</el-button>
        <el-checkbox v-model="autoRefreshEnable" size="small">自动刷新30s</el-checkbox>
        <span v-if="lastRefreshTime" class="last-refresh">更新于 {{ lastRefreshTime }}</span>
      </div>
    </div>

    <!-- 【概览总数据 独立时间筛选卡片】 日/周/月/自定义 -->
    <div class="filter-card">
      <div class="filter-title">概览统计时间</div>
      <div class="time-filter">
        <el-button
          v-for="item in overviewTimeOptions"
          :key="item.value"
          size="small"
          plain
          :type="overviewQueryParams.type === item.value ? 'primary' : 'default'"
          @click="handleOverviewTimeClick(item.value)"
        >
          {{ item.label }}
        </el-button>
        <el-date-picker
          v-if="overviewQueryParams.type === 'custom'"
          v-model="overviewDateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          style="width: 320px; margin-left: 12px"
        />
      </div>
    </div>

    <!-- 主体内容 -->
    <div class="dashboard-main">
      <!-- 左侧主内容区 -->
      <div class="main-content">
        <!-- 核心指标概览 -->
        <div class="overview-section">
          <div class="section-header">
            <h3 class="section-title">综合查询概览</h3>
            <span class="dim-tip">统计维度：{{ getOverviewDimText() }}</span>
          </div>
          <div class="overview-grid">
            <div class="overview-card">
              <div class="card-label">总调用</div>
              <div class="card-value">{{ overviewData.totalCall ?? 0 }}</div>
              <div class="card-desc">累计查询请求次数</div>
            </div>
            <div class="overview-card">
              <div class="card-label">总有效</div>
              <div class="card-value">{{ overviewData.totalValid ?? 0 }}</div>
              <div class="card-desc">有效数据记录数</div>
            </div>
            <div class="overview-card">
              <div class="card-label">审核中</div>
              <div class="card-value text-warning">{{ overviewData.auditingCount ?? 0 }}</div>
              <div class="card-desc">待人工审核数量</div>
            </div>
            <div class="overview-card">
              <div class="card-label">驳回</div>
              <div class="card-value text-danger">{{ overviewData.rejectCount ?? 0 }}</div>
              <div class="card-desc">审核未通过数量</div>
            </div>
            <div class="overview-card">
              <div class="card-label">查得(命中)</div>
              <div class="card-value text-success">{{ overviewData.hitCount ?? 0 }}</div>
              <div class="card-desc">匹配到数据的记录数</div>
            </div>
            <div class="overview-card">
              <div class="card-label">查无(未命中)</div>
              <div class="card-value">{{ overviewData.missCount ?? 0 }}</div>
              <div class="card-desc">未匹配到数据的记录数</div>
            </div>
          </div>
        </div>

        <!-- 【图表时间筛选卡片】周/月/自定义，没有日 -->
        <div class="filter-card chart-filter">
          <div class="filter-title">图表趋势时间</div>
          <div class="time-filter">
            <el-button
              v-for="item in chartTimeOptions"
              :key="item.value"
              size="small"
              plain
              :type="chartQueryParams.type === item.value ? 'primary' : 'default'"
              @click="handleChartTimeClick(item.value)"
            >
              {{ item.label }}
            </el-button>
            <el-date-picker
              v-if="chartQueryParams.type === 'custom'"
              v-model="chartDateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 320px; margin-left: 12px"
            />
          </div>
        </div>

        <!-- 趋势图区域：柱状图 + 平滑趋势线 -->
        <div class="chart-section">
          <div class="section-header">
            <h3 class="section-title">查询趋势</h3>
            <div class="section-extra">
              <el-radio-group v-model="trendMode" size="small" @change="renderChart()">
                <el-radio-button label="call">总调用</el-radio-button>
                <el-radio-button label="hit">命中趋势</el-radio-button>
                <el-radio-button label="result">结果分布</el-radio-button>
              </el-radio-group>
            </div>
          </div>
          <div ref="chartDom" class="chart-box"></div>
        </div>
      </div>

      <!-- 右侧业务信息栏 -->
      <div class="right-sidebar">
        <div class="sidebar-card">
          <div class="sidebar-title">业务说明</div>
          <div class="sidebar-list">
            <div class="sidebar-item">
              <div class="item-label">数据口径</div>
              <div class="item-value">综合查询统计</div>
            </div>
            <div class="sidebar-item">
              <div class="item-label">概览维度</div>
              <div class="item-value">{{ getOverviewDimText() }}</div>
            </div>
            <div class="sidebar-item">
              <div class="item-label">图表维度</div>
              <div class="item-value">{{ getChartDimText() }}</div>
            </div>
            <div class="sidebar-item" v-if="chartQueryParams.type === 'custom'">
              <div class="item-label">图表时间范围</div>
              <div class="item-value">{{ chartQueryParams.starttime }} ~ {{ chartQueryParams.endtime }}</div>
            </div>
          </div>
        </div>
        <div class="sidebar-card">
          <div class="sidebar-title">快捷入口</div>
          <div class="action-list">
            <el-button text type="primary" @click="goToAudit">前往订单审核</el-button>
            <el-button text type="primary" @click="refreshAll">刷新统计数据</el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ElMessage } from 'element-plus'
import { onMounted, onUnmounted, reactive, ref, watch, nextTick } from 'vue'
import { getbdcListData } from '@/api/api'
import { useLoadingStore } from '../../store/loadingstore'
import * as echarts from 'echarts'

const loadingStore = useLoadingStore()

// 概览可用选项：日、周(近七天)、月、自定义
const overviewTimeOptions = [
  { label: '日', value: 'day' },
  { label: '周(近七天)', value: 'week' },
  { label: '月', value: 'month' },
  { label: '自定义', value: 'custom' }
]
// 图表可用选项：周(近七天)、月、自定义，移除日
const chartTimeOptions = [
  { label: '周(近七天)', value: 'week' },
  { label: '月', value: 'month' },
  { label: '自定义', value: 'custom' }
]

const loadingAll = ref(false)
const autoRefreshEnable = ref(false)
let refreshTimer = null
const lastRefreshTime = ref('')
const trendMode = ref('call')
const chartDom = ref(null)
let myChart = null

// ========== 概览查询参数 ==========
const overviewQueryParams = reactive({
  type: 'day',
  starttime: '',
  endtime: ''
})
const overviewDateRange = ref([])

// ========== 图表查询参数 默认 week ==========
const chartQueryParams = reactive({
  type: 'week',
  starttime: '',
  endtime: ''
})
const chartDateRange = ref([])

// 概览数据
const overviewData = reactive({
  hitCount: 0,
  totalValid: 0,
  auditingCount: 0,
  totalCall: 0,
  rejectCount: 0,
  missCount: 0
})

// 图表时间序列数据
const chartListData = reactive({
  missCountList: [],
  totalValidList: [],
  totalCallList: [],
  hitCountList: []
})

const getOverviewDimText = () => {
  const map = { day: '日', week: '周(近七天)', month: '月', custom: '自定义' }
  return map[overviewQueryParams.type] || '-'
}
const getChartDimText = () => {
  const map = { week: '周(近七天)', month: '月', custom: '自定义' }
  return map[chartQueryParams.type] || '-'
}

/**
 * 切换概览时间
 */
const handleOverviewTimeClick = async (val) => {
  overviewQueryParams.type = val
  overviewDateRange.value = []
  overviewQueryParams.starttime = ''
  overviewQueryParams.endtime = ''
  await fetchOverviewData()
}

/**
 * 切换图表时间
 */
const handleChartTimeClick = async (val) => {
  chartQueryParams.type = val
  chartDateRange.value = []
  chartQueryParams.starttime = ''
  chartQueryParams.endtime = ''
  await fetchChartData()
}

/**
 * 获取概览数据，使用独立overviewQueryParams
 */
const fetchOverviewData = async () => {
  let params = { type: overviewQueryParams.type }
  if (overviewQueryParams.type === 'custom') {
    if (!overviewDateRange.value || overviewDateRange.value.length !== 2) {
      ElMessage.warning('请选择概览开始和结束时间')
      return
    }
    params.starttime = overviewDateRange.value[0]
    params.endtime = overviewDateRange.value[1]
  }
  try {
    const res = await getbdcListData(params)
    if (res.code == 200 && res.subcode == 200001) {
      Object.assign(overviewData, res.data)
    } else {
      ElMessage.error(res.message || '获取概览数据失败')
    }
  } catch (err) {
    console.error('fetchOverviewData error', err)
    ElMessage.error('概览接口请求异常')
  }
}

/**
 * 获取图表时间序列
 */
const fetchChartData = async () => {
  if (chartQueryParams.type === 'custom') {
    if (!chartDateRange.value || chartDateRange.value.length !== 2) {
      ElMessage.warning('请选择图表开始和结束时间')
      return
    }
    chartQueryParams.starttime = chartDateRange.value[0]
    chartQueryParams.endtime = chartDateRange.value[1]
  } else {
    chartQueryParams.starttime = ''
    chartQueryParams.endtime = ''
  }
  const params = {
    type: chartQueryParams.type,
    starttime: chartQueryParams.starttime,
    endtime: chartQueryParams.endtime
  }
  try {
    const res = await getbdcListData(params)
    if (res.code === 200 && res.subcode === 200001) {
      Object.assign(chartListData, res.data)
      await nextTick()
      renderChart()
    } else {
      ElMessage.error(res.message || '获取图表数据失败')
    }
  } catch (err) {
    console.error('fetchChartData error', err)
    ElMessage.error('图表接口请求异常')
  }
}

/**
 * 全部刷新：概览+图表
 */
const refreshAll = async () => {
  loadingAll.value = true
  try {
    await fetchOverviewData()
    await fetchChartData()
    lastRefreshTime.value = new Date().toLocaleString()
  } catch (err) {
    console.error('refreshAll error', err)
  } finally {
    loadingAll.value = false
  }
}

/**
 * 渲染图表：柱状图 + 平滑趋势线
 */
const renderChart = () => {
  if (!chartDom.value) return
  if (!myChart) {
    myChart = echarts.init(chartDom.value)
  }
  let xData = []
  let series = []

  if (trendMode.value === 'call') {
    xData = chartListData.totalCallList.map(i => i.date)
    const valArr = chartListData.totalCallList.map(i => i.count)
    series = [
      {
        name: '总调用(柱状)',
        type: 'bar',
        data: valArr,
        itemStyle: { color: '#409eff' },
        barWidth: '35%'
      },
      {
        name: '总调用(趋势)',
        type: 'line',
        smooth: true,
        data: valArr,
        itemStyle: { color: '#f56c6c' },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  } else if (trendMode.value === 'hit') {
    xData = chartListData.hitCountList.map(i => i.date)
    const hitArr = chartListData.hitCountList.map(i => i.count)
    const missArr = chartListData.missCountList.map(i => i.count)
    series = [
      {
        name: '命中(柱状)',
        type: 'bar',
        data: hitArr,
        itemStyle: { color: '#00b42a' },
        barWidth: '30%'
      },
      {
        name: '命中(趋势)',
        type: 'line',
        smooth: true,
        data: hitArr,
        itemStyle: { color: '#00b42a' },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: '未命中(柱状)',
        type: 'bar',
        data: missArr,
        itemStyle: { color: '#ff7d00' },
        barWidth: '30%'
      },
      {
        name: '未命中(趋势)',
        type: 'line',
        smooth: true,
        data: missArr,
        itemStyle: { color: '#ff7d00' },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  } else if (trendMode.value === 'result') {
    xData = chartListData.totalValidList.map(i => i.date)
    const validArr = chartListData.totalValidList.map(i => i.count)
    const callArr = chartListData.totalCallList.map(i => i.count)
    series = [
      {
        name: '总有效(柱状)',
        type: 'bar',
        data: validArr,
        itemStyle: { color: '#6b72ff' },
        barWidth: '30%'
      },
      {
        name: '总有效(趋势)',
        type: 'line',
        smooth: true,
        data: validArr,
        itemStyle: { color: '#6b72ff' },
        symbol: 'circle',
        symbolSize: 6
      },
      {
        name: '总调用(柱状)',
        type: 'bar',
        data: callArr,
        itemStyle: { color: '#409eff' },
        barWidth: '30%'
      },
      {
        name: '总调用(趋势)',
        type: 'line',
        smooth: true,
        data: callArr,
        itemStyle: { color: '#f56c6c' },
        symbol: 'circle',
        symbolSize: 6
      }
    ]
  }

  const option = {
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255,255,255,0.96)',
      textStyle: { color: '#303133' },
      borderColor: '#e5e7eb',
      borderWidth: 1
    },
    legend: {
      data: series.map(s => s.name),
      top: 4,
      left: 'center'
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '3%',
      top: 48,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: xData,
      axisLine: { lineStyle: { color: '#e5e7eb' } }
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f0f2f5' } }
    },
    series
  }
  myChart.setOption(option)
}

const setupAutoRefresh = () => {
  if (refreshTimer) clearInterval(refreshTimer)
  refreshTimer = null
  if (autoRefreshEnable.value) {
    refreshTimer = setInterval(() => refreshAll(), 30000)
  }
}

watch(autoRefreshEnable, setupAutoRefresh)

// 概览自定义日期选中自动请求
watch(overviewDateRange, (newVal) => {
  if (overviewQueryParams.type === 'custom' && newVal && newVal.length === 2) {
    fetchOverviewData()
  }
})
// 图表自定义日期选中自动请求
watch(chartDateRange, (newVal) => {
  if (chartQueryParams.type === 'custom' && newVal && newVal.length === 2) {
    fetchChartData()
  }
})

const resizeChart = () => {
  myChart && myChart.resize()
}

onMounted(async () => {
  await refreshAll()
  window.addEventListener('resize', resizeChart)
})

onUnmounted(() => {
  if (refreshTimer) clearInterval(refreshTimer)
  window.removeEventListener('resize', resizeChart)
  if (myChart) {
    myChart.dispose()
    myChart = null
  }
})

const goToAudit = () => {
  ElMessage.info('跳转至订单审核页')
}
</script>

<style lang="scss" scoped>
.dashboard-page {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f7f9fc 0%, #eef2ff 100%);
  padding: 18px 22px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap:16px;
    margin-bottom:14px;

    .dashboard-title {
      margin:0;
      font-size:22px;
      font-weight:700;
      color:#1e293b;
    }
    .header-right{
      display:flex;
      align-items:center;
      flex-wrap:wrap;
      gap:12px;
      .last-refresh{
        font-size:12px;
        color:#64748b;
      }
    }
  }

  .filter-card{
    background:#ffffff;
    border-radius:12px;
    padding:14px 18px;
    box-shadow:0 1px 3px rgba(0,0,0,0.04);
    margin-bottom:18px;
    display:flex;
    align-items:center;
    flex-wrap:wrap;
    gap:16px;

    .filter-title{
      font-size:15px;
      font-weight:600;
      color:#1e293b;
    }
    .time-filter{
      display:flex;
      align-items:center;
      gap:8px;
    }
  }
  .chart-filter{
    margin-bottom:14px;
  }

  .dashboard-main {
    display: grid;
    grid-template-columns: 1fr 300px;
    gap: 18px;
    height: calc(100% - 240px);

    .main-content {
      display: flex;
      flex-direction: column;
      gap: 18px;
      min-width: 0;

      .overview-section {
        background: #ffffff;
        border-radius: 12px;
        padding: 18px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        .section-header {
          display:flex;
          justify-content:space-between;
          align-items:center;
          margin-bottom: 16px;
          .section-title {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
          }
          .dim-tip{
            font-size:13px;
            color:#64748b;
          }
        }
        .overview-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          .overview-card {
            padding: 16px;
            border-radius: 10px;
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border: 1px solid #e2e8f0;
            transition: 0.25s ease;
            &:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
            }
            .card-label {
              font-size: 13px;
              color: #64748b;
              margin-bottom: 8px;
            }
            .card-value {
              font-size: 28px;
              font-weight: 700;
              color: #1e293b;
              line-height: 1.2;
              margin-bottom: 6px;
            }
            .card-desc {
              font-size: 12px;
              color: #94a3b8;
            }
          }
        }
      }

      .chart-section {
        flex: 1;
        background: #ffffff;
        border-radius: 12px;
        padding: 18px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        display: flex;
        flex-direction: column;
        min-height: 360px;
        .section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          .section-title {
            margin: 0;
            font-size: 16px;
            font-weight: 600;
            color: #1e293b;
          }
        }
        .chart-box {
          flex:1;
          width:100%;
          min-height:300px;
        }
      }
    }

    .right-sidebar {
      display: flex;
      flex-direction: column;
      gap: 18px;
      .sidebar-card {
        background: #ffffff;
        border-radius: 12px;
        padding: 16px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
        .sidebar-title {
          margin: 0 0 14px 0;
          font-size: 15px;
          font-weight: 600;
          color: #1e293b;
        }
        .sidebar-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          .sidebar-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            .item-label {
              font-size: 13px;
              color: #64748b;
            }
            .item-value {
              font-size: 13px;
              color: #1e293b;
              font-weight: 500;
            }
          }
        }
        .action-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
      }
    }
  }

  @media (max-width: 1200px) {
    .dashboard-main {
      grid-template-columns: 1fr;
      height:auto !important;
      .right-sidebar {
        flex-direction: row;
        .sidebar-card {
          flex: 1;
        }
      }
    }
    .overview-grid {
      grid-template-columns: repeat(2, 1fr) !important;
    }
  }
  @media (max-width: 768px) {
    padding: 12px 14px;
    .page-header{
      flex-direction:column;
      align-items:flex-start;
    }
    .overview-grid {
      grid-template-columns: 1fr !important;
    }
    .right-sidebar {
      flex-direction: column !important;
    }
  }
}

.text-success {
  color: #00b42a;
}
.text-warning {
  color: #ff7d00;
}
.text-danger {
  color: #f53f3f;
}
</style>
